// /neon-signs-nyc/ (ProjectServicePage). Was a Contentful page entry until
// 2026-11-04; gatsby-node now skips it.
// DEMAND TEST: .seo/truth.md has no neon row, so under the owner growth
// policy (traffic first, rule 2) this page tests demand. It describes LED
// neon (flexible LED tube on a laser-cut acrylic backer), the kind brands use
// for pop-ups; hand-bent glass neon is pointed to a glass neon shop. No
// price, turnaround or past neon project is published: "quoted per project".
// The old page's "2–3 days" and "visit our store" claims had no source and
// are gone. The neon photos were on the old page with no project details, so
// they're captioned as reference photos of sign styles.
// GSC Aug–Oct 2026: 0 clicks, 60 impressions. Queries: "custom neon signs
// nyc" (pos 21), "neon signs nyc" (29), "neon signs ny" (32). Keyword data
// (US): "custom neon signs" 33,100/mo (CPC $9.55), "led neon signs" 8,100,
// "neon signs nyc" 390, "custom neon signs nyc" 70 (CPC $16.70).
const { reviewedDate, studios, photo } = require('./shared')

const reference =
  'Reference photo of a neon sign style, not a PrintAWorld project.'

const photos = {
  rivington: photo(
    '2spiiS5V1AKvFR5lYFtMOx',
    '4ac64ab73aec3f84361466fcc9f48a09/neon-sign.jpeg',
    1171,
    878,
    'A red neon street sign reading Rivington Street with an arrow, glowing at night',
    'Neon lettering style',
    reference
  ),
  crafts: photo(
    '6KEiybbMz5t8LMKUrUELOo',
    '37375ac9d313ecfc8e36fac724139bb3/neon_sign.jpg',
    1545,
    1374,
    'Pink neon sign repeating the word CRAFTS three times on a wall',
    'Repeated wordmark',
    reference
  ),
  simply: photo(
    '2QNIPdifQ2Z2N9WeVzsvWb',
    '1fd26855b96574ba3eeaa5ea73bb6209/simply_neon_sign.jpg',
    819,
    1000,
    'White neon sign reading simply mounted on an office wall above a desk',
    'Office wall sign',
    reference
  ),
  printShop: photo(
    '20svmBfjZKOD9C0YpnkSiN',
    'baa7dd1ffb97c8877215c3f96de218e7/neon_sign.jpeg',
    1206,
    1334,
    'Multicolor neon window sign reading Printing and Color Copy',
    'Two-line window sign',
    reference
  ),
  script: photo(
    '1QhPQGSl4jC2GDlewI1Bsc',
    '884bd2d722ffcf364e03c4af3cf3a121/true_romance.jpg',
    800,
    600,
    'Orange script neon sign reading True Romance',
    'Script lettering',
    reference
  ),
}

const page = {
  path: '/neon-signs-nyc/',
  title: 'Custom Neon Signs NYC | LED Neon Logo Signs',
  description:
    'Custom neon signs in NYC for brands, pop-ups and events: LED neon logos and wordmarks on laser-cut acrylic, made in Brooklyn and Queens. Quoted per project.',
  heading: 'Custom Neon Signs in NYC',
  eyebrow: 'LED neon logo signs · Brooklyn & Queens',
  answer:
    'PrintAWorld makes custom LED neon signs for New York brands, agencies and businesses: logos, wordmarks and lines of text for pop-ups, events, retail, offices and photo walls. LED neon is flexible LED tube that glows like glass neon, mounted on a laser-cut acrylic backer, so it’s lighter and less fragile to move and hang. Send your logo, the size and your date; each sign is quoted per project. Our studios are in Brooklyn and Glendale, Queens, by appointment; we deliver in NYC and ship across the U.S.',
  hero: 'rivington',
  reviewedDate,
  crumbs: [
    { name: '3D signs', path: '/3d-signs-nyc/' },
    { name: 'Neon signs NYC' },
  ],
  service: {
    name: 'Custom Neon Signs NYC',
    serviceType: 'Custom LED neon signs',
    areaServed: [
      { '@type': 'City', name: 'New York City' },
      { '@type': 'Country', name: 'United States' },
    ],
    description:
      'Custom LED neon logo and text signs on laser-cut acrylic backers for New York brands, pop-ups, events, retail and offices, quoted per project, with pickup, NYC delivery or shipping across the U.S.',
  },
  quoteLabel: 'Get a neon sign quote',
}

const intro = {
  eyebrow: 'Custom neon signs NYC',
  heading: 'What is a custom LED neon sign?',
  paragraphs: [
    'A custom neon sign today is usually LED neon: a flexible LED tube with a diffused cover that bends to your letters or logo and glows like glass neon. It’s mounted on a clear or colored acrylic backer cut to the shape of the design, and it runs on a plug-in power supply.',
    'For brands that means a sign you can carry into a pop-up, hang on a photo wall and pack up again. We cut the backers on our [laser cutters](/services/laser-cutting/), and can print graphics on them or pair the neon with [dimensional 3D letters](/3d-signs-nyc/).',
  ],
}

const glance = {
  heading: 'Custom neon signs in NYC at a glance',
  rows: [
    ['Studios', studios],
    ['Sign type', 'LED neon on a laser-cut acrylic backer'],
    ['Who it’s for', 'Brands, agencies, retailers, offices and event teams'],
    ['Common uses', 'Pop-ups, events, store windows, offices and photo walls'],
    ['Artwork', 'A vector logo (AI, EPS, SVG or PDF), or text and a font'],
    ['Mounting', 'Wall, hanging or freestanding, as quoted'],
    ['Price', 'Quoted per project'],
    ['Turnaround', 'Quoted per project. Tell us your event date first.'],
    [
      'Delivery',
      'Pickup by appointment, delivery in NYC, or shipping across the U.S.',
    ],
    ['Not offered', 'Hand-bent glass neon'],
  ],
}

const work = {
  eyebrow: 'Sign styles',
  heading: 'Neon sign styles you can ask for',
  photos: ['crafts', 'simply', 'printShop', 'script'],
}

const options = {
  eyebrow: 'Your options',
  heading: 'Where can you get a custom neon sign made in NYC?',
  items: [
    {
      label: 'O1',
      title: 'A glass neon shop',
      body: 'Hand-bent glass tubes filled with neon or argon. The classic look; heavier and more fragile, and usually priced higher.',
    },
    {
      label: 'O2',
      title: 'An online LED neon store',
      body: 'Pick text, a font and a color online and wait for shipping. Good for simple text signs; logos and brand colors vary.',
    },
    {
      label: 'O3',
      title: 'A fabrication studio, like ours',
      body: 'A person reviews your logo and space, and the neon can be combined with UV-printed backers or 3D letters. Pick up in Brooklyn or Queens, or have it delivered.',
    },
  ],
}

const offer = {
  eyebrow: 'What we make',
  heading: 'What kinds of custom neon signs can we make?',
  index: '02 / THE SIGNS',
  items: [
    {
      label: 'N1',
      title: 'Logo neon signs',
      body: 'Your logo traced in LED neon, with the backer cut to its outline or to a simple shape.',
    },
    {
      label: 'N2',
      title: 'Wordmarks and taglines',
      body: 'A brand name, a hashtag or a line of copy in a font that matches your guidelines.',
    },
    {
      label: 'N3',
      title: 'Pop-up and event signs',
      body: 'Signs built to travel: light backers, a plug-in power supply and a freestanding or hanging mount.',
    },
    {
      label: 'N4',
      title: 'Printed backers',
      body: 'Neon over a UV-printed acrylic backer, for full-color graphics behind the light.',
    },
    {
      label: 'N5',
      title: 'Neon with 3D letters',
      body: 'LED neon paired with dimensional letters or a sculpted logo for a bigger brand wall.',
    },
    {
      label: 'N6',
      title: 'Several matching signs',
      body: 'The same sign for a run of stores, events or offices, quoted per piece.',
    },
  ],
}

const contrast = {
  eyebrow: 'LED or glass?',
  heading: 'LED neon vs glass neon: which do you need?',
  paragraphs: [
    'LED neon is the practical choice for most brand signs: it’s lighter, runs on a plug-in power supply, and survives being moved between events. It also reads well on camera.',
    'Glass neon is hand-bent tube with a warmer, deeper glow and real craft, and it costs more and needs careful handling. If you want glass, a traditional neon shop is the right call; we quote LED neon.',
  ],
  links: [
    ['Custom 3D signs NYC', '/3d-signs-nyc/'],
    ['Laser cutting in NYC', '/laser-cutting-service-nyc/'],
  ],
}

const steps = {
  eyebrow: 'How it works',
  heading: 'How our custom neon sign service works',
  items: [
    {
      title: 'Send the logo and size',
      body: 'Vector logo or text and font, width, colors, where it will hang, and your date.',
    },
    {
      title: 'We review and quote',
      body: 'A person checks the design for neon (tight details may need simplifying) and sends a written quote.',
    },
    {
      title: 'Approve the layout',
      body: 'You approve the line layout, colors and backer shape before anything is made.',
    },
    {
      title: 'Pickup or delivery',
      body: 'Pick up by appointment, delivery in NYC, or packed and shipped across the U.S.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Is a custom LED neon sign right for you?',
  good: [
    'Brand logos and wordmarks for pop-ups, launches and events',
    'Office, studio and retail interior signs',
    'Photo walls and backdrops that need to look good on camera',
    'Neon combined with printed backers or 3D letters',
    'Several matching signs for a store rollout or tour',
  ],
  other: [
    'Hand-bent glass neon: a traditional neon shop',
    'A sign for one night only: a neon rental company may cost less',
    'Permitted outdoor storefront signs or electrical work: a licensed sign company',
    'A simple personal name sign: an online LED neon store is cheaper',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your sign',
  heading: 'How much does a custom neon sign cost in NYC?',
  lead: {
    label: 'Custom neon signs are',
    value: 'Quoted per project',
    note: 'Send the logo, width and date for a written quote.',
  },
  rowsHeading: 'What sets the price of a neon sign',
  rows: [
    ['Size', 'Width and the total length of neon line'],
    ['Detail', 'Logos with many small parts take more work'],
    ['Colors', 'Each color is its own run of LED neon'],
    ['Backer', 'Clear, colored, printed or cut to the logo outline'],
    ['Mounting', 'Wall, hanging or a freestanding stand'],
    ['Quantity and timing', 'Matching sets, and how soon you need them'],
  ],
  note: 'Installation and electrical work aren’t included unless your quote lists them.',
}

const logistics = {
  eyebrow: 'Pickup & delivery',
  heading: 'Neon signs near you: our Brooklyn and Queens studios',
  paragraphs: [
    'Our studios are at 616 Berriman Street, Brooklyn and 74-12 88th Street, Glendale, Queens. Visits and pickups are by appointment, so send your logo first. We can also deliver in New York or pack the sign and ship it across the U.S.',
  ],
  aside: {
    eyebrow: 'Have an event date?',
    title: 'Tell us the date first.',
    body: 'Timing is quoted per project and depends on the design, size and quantity. Share your pop-up or event date in your request and the quote confirms it.',
  },
}

const related = {
  heading: 'More sign services',
  links: [
    ['Custom 3D signs NYC', '/3d-signs-nyc/'],
    ['Laser cutting', '/services/laser-cutting/'],
    ['Laser cutting in NYC', '/laser-cutting-service-nyc/'],
    ['CNC routing', '/cnc-routing-service/'],
  ],
}

const faqs = [
  {
    question: 'How much does a custom neon sign cost in NYC?',
    answer:
      'At PrintAWorld, custom neon signs are quoted per project. Size, the length of neon line, detail, colors, the backer and mounting set the price. Send your logo, the width and your date for a written quote.',
  },
  {
    question: 'Are your custom neon signs real neon or LED?',
    answer:
      'LED neon: a flexible LED tube that glows like glass neon, on a laser-cut acrylic backer. It’s lighter, less fragile and runs on a plug-in power supply. We don’t make hand-bent glass neon; a traditional neon shop does.',
  },
  {
    question: 'Can you make a neon sign of our logo?',
    answer:
      'Yes. Send a vector logo (AI, EPS, SVG or PDF). We check it for neon, since very small details may need simplifying, and you approve the layout and colors before anything is made.',
  },
  {
    question: 'Where can I get a custom neon sign made in NYC?',
    answer:
      'You can use a glass neon shop, an online LED neon store, or a fabrication studio. PrintAWorld’s studios are in Brooklyn (616 Berriman Street) and Glendale, Queens (74-12 88th Street), by appointment; we quote LED neon signs and can pair them with printed backers or 3D letters.',
  },
  {
    question: 'How long does a custom neon sign take?',
    answer:
      'Timing is quoted per project and depends on the design, size and quantity. Put your event or opening date in your request and the written quote confirms the schedule.',
  },
  {
    question: 'Can a neon sign go outside?',
    answer:
      'Outdoor signs need extra review for weather, mounting and power. Tell us where it will hang; permitted storefront signs and electrical work belong with a licensed sign company.',
  },
  {
    question: 'Do you install neon signs?',
    answer:
      'Installation isn’t included unless your quote lists it. Most LED neon signs hang from a wall or ceiling or stand on their own, and plug into a power supply.',
  },
  {
    question: 'Do you ship neon signs outside New York?',
    answer:
      'Yes. We pack signs for the trip and ship across the U.S., or deliver in NYC.',
  },
]

const checklist = [
  'Vector logo, or the text and font',
  'Width and the colors you want',
  'Backer: clear, colored, printed or cut to shape',
  'Wall, hanging or freestanding; indoor or outdoor',
  'Quantity, delivery ZIP and your event date',
]

module.exports = {
  key: 'neon',
  demandTest: true,
  page,
  photos,
  intro,
  glance,
  work,
  options,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'What do people ask about custom neon signs in NYC?',
  checklist,
  quote: {
    eyebrow: 'Let’s light your logo',
    heading: ['Send us your logo.', 'We’ll send a price.'],
    body: 'Send the logo, the size and your date. A person checks it and replies with a written quote.',
  },
}
